import React, { memo, useRef } from "react";
import { useDrag, useDrop, DropTargetMonitor } from "react-dnd";
import { Grid, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import classNames from "classnames";
import SettingsIcon from "@mui/icons-material/Settings";
import FlightIcon from "@mui/icons-material/Flight";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { IAircraft } from "@vmosolutions/oc-interfaces";
import { AircraftMaintenanceCounters } from "src/components/AircraftContext/AircraftMaintenanceCounters";
import { AircraftStatus } from "./AircraftStatus/AircraftStatus";

/** react-dnd item type for reordering aircraft rows while the quick filter is applied (ENG-715). */
export const QUICK_FILTER_AIRCRAFT_DND_TYPE = "QUICK_FILTER_AIRCRAFT";

/**
 * Resolves one entry of an aircraft's configured identifier order (identityDisplayOrder). Entries can be nested paths
 * such as "aircraftType.title" (see the admin aircraft form), so the path is walked rather than read as a single key.
 * Anything that is not text or a number shows as empty.
 */
export const aircraftIdentityPart = (aircraft: IAircraft, path: string): string => {
  const value = path
    .split(".")
    .reduce<unknown>(
      (node, key) => (node && typeof node === "object" ? (node as Record<string, unknown>)[key] : undefined),
      aircraft
    );
  return typeof value === "string" || typeof value === "number" ? String(value) : "";
};

interface IAircraftDragItem {
  id: string;
  index: number;
}

export interface IAircraftIdentityRowProps {
  aircraft: IAircraft;
  /** Position of this row in the currently displayed aircraft list. */
  index: number;
  windowId: string;
  /** Row is highlighted / part of the aircraft selection used for bulk assignment. */
  selected: boolean;
  /** Quick filter "free mode": rows can be dragged into a custom vertical order. */
  reorderable: boolean;
  showMaintenanceCounters: boolean;
  showAircraftStatus: boolean;
  /** Chip click: selects the aircraft (shift+click selects a range). */
  onSelect: (aircraftId: string, event: React.MouseEvent) => void;
  onOpenContextPanel: (aircraftId: string, event: React.MouseEvent, tabKey?: string) => void;
  /** Drag reorder: move `fromAircraftId` to the position of `toAircraftId`. */
  onMove: (fromAircraftId: string, toAircraftId: string) => void;
  /** Fired while a row is dragged over this row, with the pointer's clientY, so the parent can auto-scroll. */
  onDragHover?: (clientY: number) => void;
}

/**
 * One aircraft "chip" in the flight display aircraft column, plus its optional maintenance counter and live status
 * cells. Extracted from FlightTimeline so each row can own its react-dnd hooks for quick filter reordering.
 * Styling lives in FlightTimeline's timelineBodyStyles (targets the class names used here).
 */
export const AircraftIdentityRow = memo((props: IAircraftIdentityRowProps): JSX.Element => {
  const {
    aircraft,
    index,
    windowId,
    selected,
    reorderable,
    showMaintenanceCounters,
    showAircraftStatus,
    onSelect,
    onOpenContextPanel,
    onMove,
    onDragHover,
  } = props;
  const theme = useTheme();
  const cardRef = useRef<HTMLDivElement>(null);

  // IAircraft._id is typed any in the interfaces package; pin it to string once so every consumer below is typed.
  const aircraftId = aircraft._id as string;
  const { aircraftType, registration, fleetNumber, groupColour, identityDisplayOrder } = aircraft;
  const acType = aircraftType?.codeICAO ? aircraftType.codeICAO : aircraftType?.title ? aircraftType.title : "";
  const acReg = registration ? registration : "";
  const acFleet = fleetNumber ? fleetNumber : "";
  const [firstKey, secondKey] = identityDisplayOrder ?? [];
  const firstIdentifier = firstKey ? aircraftIdentityPart(aircraft, firstKey) : acReg;
  const secondIdentifier = secondKey ? aircraftIdentityPart(aircraft, secondKey) : acFleet;
  const barColour = groupColour ?? theme.custom.timeline.groupColumnDivider;

  // Drag source: the whole card can be dragged while in free mode.
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: QUICK_FILTER_AIRCRAFT_DND_TYPE,
      item: (): IAircraftDragItem => ({ id: aircraftId, index }),
      canDrag: reorderable,
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [aircraftId, index, reorderable]
  );

  // Drop target: reorder live as the dragged row crosses this row's vertical midpoint (classic react-dnd sortable).
  const [, drop] = useDrop<IAircraftDragItem>(
    () => ({
      accept: QUICK_FILTER_AIRCRAFT_DND_TYPE,
      canDrop: () => reorderable,
      hover(item: IAircraftDragItem, monitor: DropTargetMonitor) {
        if (!reorderable || !cardRef.current) return;
        const clientOffset = monitor.getClientOffset();
        if (!clientOffset) return;
        onDragHover?.(clientOffset.y);
        if (item.id === aircraftId) return;

        const dragIndex = item.index;
        const hoverIndex = index;
        const rect = cardRef.current.getBoundingClientRect();
        const hoverMiddleY = (rect.bottom - rect.top) / 2;
        const hoverClientY = clientOffset.y - rect.top;
        // Only move once the pointer has crossed half of the hovered row, to avoid flickering back and forth.
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

        onMove(item.id, aircraftId);
        // Mutating the monitor item is the documented way to keep the drag index in step with the live reorder.
        item.index = hoverIndex;
      },
    }),
    [aircraftId, index, reorderable, onMove, onDragHover]
  );

  drag(drop(cardRef));

  return (
    <Grid container wrap="nowrap" className="aircraft_row">
      <Grid
        className={classNames(
          "aircraft_details",
          selected && "selected",
          reorderable && "reorderable",
          isDragging && "dragging"
        )}
        style={{ borderRightColor: barColour }}
        item
      >
        <div
          ref={cardRef}
          onClick={(event: React.MouseEvent) => onSelect(aircraftId, event)}
          onContextMenu={(event: React.MouseEvent) => onOpenContextPanel(aircraftId, event)}
          style={{
            borderRightWidth: "4px",
            borderRightStyle: "solid",
            borderRightColor: barColour,
          }}
        >
          <div className="aircraft_text">
            {reorderable && (
              <Tooltip title="Drag to reorder" placement="right" enterDelay={800} arrow>
                <DragIndicatorIcon className="aircraft_drag_handle" />
              </Tooltip>
            )}
            <span className="aircraft_reg">{firstIdentifier}</span>
            {secondIdentifier && <span className="aircraft_fleet">{secondIdentifier}</span>}
            <span className="aircraft_type">{acType}</span>
          </div>
          <div className="aircraft_icons" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <Tooltip title="Maintenance" placement="top" arrow>
              <span>
                <SettingsIcon
                  className="aircraft_icon"
                  onClick={(e: React.MouseEvent) => onOpenContextPanel(aircraftId, e, "maintenance")}
                />
              </span>
            </Tooltip>
            <Tooltip title="OpsLink (coming soon)" placement="top" arrow>
              <span>
                <FlightIcon className="aircraft_icon aircraft_icon_disabled" />
              </span>
            </Tooltip>
          </div>
        </div>
      </Grid>
      {showMaintenanceCounters && <AircraftMaintenanceCounters aircraftId={aircraftId} />}
      {showAircraftStatus && (
        <Grid item className="aircraft_status">
          <AircraftStatus windowId={windowId} aircraftId={aircraftId} isSelected={selected} groupColor={barColour} />
        </Grid>
      )}
    </Grid>
  );
});
AircraftIdentityRow.displayName = "AircraftIdentityRow";
